export type NameCheckReason = 'empty' | 'too-short' | 'too-long' | 'charset' | 'blocked';

export interface NameCheck {
  ok: boolean;
  name: string;
  reason?: NameCheckReason;
}

const SUBSTRING_BLOCKED = [
  'fuck',
  'fuk',
  'phuck',
  'fck',
  'shit',
  'sht',
  'bitch',
  'btch',
  'cunt',
  'pussy',
  'whore',
  'slut',
  'nigger',
  'nigga',
  'faggot',
  'retard',
  'penis',
  'vagina',
  'anus',
  'porn',
  'hitler',
  'nazi',
  'pedo',
  'molest',
  'jizz',
  'wanker',
  'bollocks',
  'arsehole',
  'asshole',
  'bastard',
  'kontol',
  'memek',
  'ngentot',
  'ngentod',
  'pepek',
  'jancok',
  'jancuk',
  'bangsat',
  'bajingan',
  'brengsek',
  'goblok',
  'tolol',
  'pukimak',
  'kimak',
  'kampret',
  'tahi',
  'monyet',
  'bedebah',
  'keparat',
  'sialan',
  'laknat',
];

const TOKEN_BLOCKED = ['ass', 'cock', 'dick', 'tits', 'cum', 'sex', 'fag', 'rape', 'asu', 'bego', 'puki'];

const TOKEN_RESERVED = [
  'admin',
  'administrator',
  'mod',
  'moderator',
  'owner',
  'dev',
  'developer',
  'support',
  'staff',
  'official',
  'system',
  'root',
  'crazyclock',
  'null',
  'undefined',
];

const LEET: Record<string, string> = {
  '0': 'o',
  '1': 'i',
  '3': 'e',
  '4': 'a',
  '5': 's',
  '7': 't',
  '8': 'b',
  '9': 'g',
  '@': 'a',
  $: 's',
  '!': 'i',
  '+': 't',
};

function collapseRepeats(value: string): string {
  return value.replace(/(.)\1+/g, '$1');
}

function normalizeName(value: string): { compact: string; collapsed: string; tokens: string[] } {
  const lowered = value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '');
  let mapped = '';
  for (const ch of lowered) {
    mapped += LEET[ch] ?? ch;
  }
  const compact = mapped.replace(/[^a-z0-9]/g, '');
  const tokens = mapped
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter((token) => token.length > 0);
  return { compact, collapsed: collapseRepeats(compact), tokens };
}

export function isNameBlocked(value: string): boolean {
  const { compact, collapsed, tokens } = normalizeName(value);
  if (compact.length === 0) {
    return false;
  }
  for (const word of SUBSTRING_BLOCKED) {
    if (compact.includes(word) || collapsed.includes(word)) {
      return true;
    }
  }
  for (const word of TOKEN_BLOCKED) {
    if (compact === word || collapsed === word) {
      return true;
    }
  }
  for (const token of tokens) {
    const collapsedToken = collapseRepeats(token);
    if (TOKEN_BLOCKED.includes(token) || TOKEN_BLOCKED.includes(collapsedToken)) {
      return true;
    }
    if (TOKEN_RESERVED.includes(token) || TOKEN_RESERVED.includes(collapsedToken)) {
      return true;
    }
  }
  return false;
}

export function checkPlayerName(raw: string): NameCheck {
  const name = raw.trim().replace(/\s+/g, ' ');
  if (name.length === 0) {
    return { ok: false, name, reason: 'empty' };
  }
  if (name.length < 2) {
    return { ok: false, name, reason: 'too-short' };
  }
  if (name.length > 16) {
    return { ok: false, name, reason: 'too-long' };
  }
  if (!/^[A-Za-z0-9 _-]+$/.test(name)) {
    return { ok: false, name, reason: 'charset' };
  }
  if (isNameBlocked(name)) {
    return { ok: false, name, reason: 'blocked' };
  }
  return { ok: true, name };
}

export function nameErrorMessage(reason: NameCheckReason | undefined): string {
  switch (reason) {
    case 'blocked':
      return "let's keep it friendly — try another name";
    case 'charset':
      return 'letters, numbers, spaces, - and _ only';
    case 'too-short':
      return 'at least 2 characters';
    case 'too-long':
      return '16 characters max';
    default:
      return 'pick a name first';
  }
}
